import { defineStore } from 'pinia'
import { signIn, signOut, retrieveUserInfo, retrieveSuperUserAddress, retrieveUserAllowOrderStatus } from '@/api/user'
import { buriedPointUtil, gaAnalytics, isMobile } from '@/utils/utils'
import { fetchData } from '@/utils/fetch'
import storage from '@/utils/storage'

export const useUserStore = defineStore('bestdealer-user', () => {
  const userInfo = reactive({
    tk: null, // token
    uid: null, // 用户 uid
    email: null,
    name: null,
    complete: null, // 存储已完成引导的功能，1-Search，2-Category，3-Sourcing，4-Product，5-Language
    allowOrderSwitch: null // 允许下单开关 0-否 1-是
  })

  const isLogged = computed(() => userInfo.tk !== null)

  const initUserInfo = async () => {
    const { code, data, msg } = await retrieveUserInfo()
    if (code === 200) {
      Object.assign(userInfo, data)
    } else {
      /** 获取用户信息接口报错则直接登出 */
      clearUserInfo()
      throw new Error(msg)
    }
  }

  const initUserAllowOrderStatus = async () => {
    const { code, data } = await retrieveUserAllowOrderStatus()
    if (code === 200) {
      userInfo.allowOrderSwitch = data
    }
  }

  const initSuperUserInfo = async () => {
    const { code, data } = await retrieveSuperUserAddress()
    if (code === 200) {
      if (data) {
        // TODO: 抽象 country list 列表，并且返回
        const cacheCountryList = JSON.parse(localStorage.getItem('cacheCountryList'))
        let currentCountry
        let phoneCodeCountry
        if (cacheCountryList) {
          currentCountry = cacheCountryList.data.find(item => item.countryName === data.country)
          phoneCodeCountry = cacheCountryList.data.find(item => item.phoneCode === data.phoneCode && item.countryName === data.country)
        } else {
          const { code: code2, data: countryList } = await fetchData('/dsp-app/alibaba-common/country')
          if (code2 === 200) {
            currentCountry = countryList.find(item => item.countryName === data.country)
            phoneCodeCountry = countryList.find(item => item.phoneCode === data.phoneCode && item.countryName === data.country)
          }
        }
        const address = {
          ...data,
          phoneValue: data.phone,
          streetAddress: data.streetAddr,
          aptEtc: data.extAddr,
          postCode: data.zip,
          searchImg: currentCountry?.countryUrl,
          searchCountryText: currentCountry?.countryName,
          placeholderSearchText: currentCountry?.countryName,
          areaCodeImg: phoneCodeCountry ? phoneCodeCountry.countryUrl : currentCountry?.countryUrl,
          placeholderAreaCodeText: currentCountry?.countryName
        }
        if (address.country === 'Brazil') {
          address.cpfNumber = data.extension
        }
        if (address.country === 'Chile') {
          address.rutNumber = data.extension
        }
        storage.set('addressForm', address)
      }
    }
  }

  /**
   * ! 除非你清楚你在做什么，否则不要直接在外层使用该方法
   */
  const clearUserInfo = () => {
    Object.assign(userInfo, {
      tk: null,
      uid: null,
      email: null,
      name: null,
      complete: null,
      allowOrderSwitch: null
    })
  }

  /**
   * 账户登录
   * @param {*} params
   */
  const login = async (params) => {
    const { code, data, msg } = await signIn({
      registerTerminal: isMobile() ? 2 : 1,
      saasChannel: 12,
      ...params
    })
    if (code === 200) {
      if (data?.newUser) {
        buriedPointUtil({ eventId: isMobile() ? 'MPLG601' : 'PLG601' })
        if (params.loginType === 'google') {
          gaAnalytics(isMobile() ? 'mp_register_success' : 'p_register_success', 'continue_with_google', {
            userId: data.uid,
            newUser: data?.newUser,
            bdUserId: data.uid,
            loginType: 'continue_with_google'
          })
        } else {
          gaAnalytics(isMobile() ? 'mp_register_success' : 'p_register_success', 'account', {
            userId: data.uid,
            newUser: data?.newUser,
            bdUserId: data.uid,
            loginType: 'account'
          })
        }
      }

      userInfo.tk = data.tk
      userInfo.uid = data.uid

      await initUserInfo()

      if (params.superuser) {
        await initSuperUserInfo()
      }

      return data
    } else {
      throw new Error(msg)
    }
  }

  const loginWithSSO = async (tk) => {
    userInfo.tk = tk

    await initUserInfo()
  }

  const logout = async () => {
    signOut()
    clearUserInfo()
  }

  return { isLogged, userInfo, login, loginWithSSO, logout, initUserInfo, clearUserInfo, initUserAllowOrderStatus }
}, {
  persist: true,
  sync: true
})
